import { Action } from '@/_core/fluxstore/types';
import { snackbar } from '@/_core/app-components/use';
import { TrashOperationResultCode } from '../../../api/queries';
import { RestoreDeletedCardResult } from '../../../api/commands';
import { createRestoreDeletedCardCommand } from '../../../api/commands/factory/createRestoreDeletedCardCommand';
import { StorymapState, TrashRow } from '../../types';
import { useStorymapStore } from '../../useModule';
import { InitTrashedCards } from './initTrashedCards';
import { MergeRestoredCardsFromTrash } from './mergeRestoredCardsFromTrash';
import { sendTrashCommand } from './sendTrashCommand';
import { TrySelectRestoredCardFromTrash } from './trySelectRestoredCardFromTrash';

const { registerMiddleware, registerReducer, dispatch } = useStorymapStore();

export class RestoreCardFromTrash extends Action<StorymapState> {
    public static ACTION_TYPE = 'RestoreCardFromTrash';
    public wasSelected = false;

    public constructor(public row: TrashRow) {
        super(RestoreCardFromTrash.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: RestoreCardFromTrash): Promise<boolean> => {
    if (!action.row || state.trashState.isLoading || state.trashState.isRowOperationInProgress) {
        return true;
    }

    const token = state.trashState.rowOperationToken + 1;
    state.trashState.rowOperationToken = token;
    state.trashState.isRowOperationInProgress = true;

    try {
        const result = await sendTrashCommand<RestoreDeletedCardResult>(await createRestoreDeletedCardCommand(action));

        if (result?.IsSuccess) {
            await dispatch(new MergeRestoredCardsFromTrash(result.NextGenRestoredCards));

            if (state.trashState.isDialogOpen && state.trashState.rowOperationToken === token) {
                removeRow(state, action.row.id);
            }

            const selectAction = new TrySelectRestoredCardFromTrash(result.RestoredCardId);
            await dispatch(selectAction);

            if (selectAction.wasSelected) {
                action.wasSelected = true;
                snackbar.showSuccess('Card restored and selected.');
            } else {
                snackbar.showSuccess(`${result.RestoredCardName || action.row.title} restored.`);
                if (state.trashState.isDialogOpen && state.trashState.rowOperationToken === token) {
                    await dispatch(new InitTrashedCards());
                }
            }

            return true;
        }

        if (isStaleCode(result?.ResultCode)) {
            if (state.trashState.isDialogOpen && state.trashState.rowOperationToken === token) {
                removeRow(state, action.row.id);
            }
            snackbar.showInfo(result?.Message || 'The selected row is no longer in trash.');
            return true;
        }

        snackbar.showError(result?.Message || 'Restore failed. Please try again.');
        return true;
    } finally {
        if (state.trashState.isDialogOpen && state.trashState.rowOperationToken === token) {
            state.trashState.isRowOperationInProgress = false;
        }
    }
};

const reducer = (): void => {
    // State is updated in middleware because command result variants drive different transitions.
};

const isStaleCode = (resultCode: TrashOperationResultCode): boolean =>
    resultCode === TrashOperationResultCode.AlreadyRestored ||
    resultCode === TrashOperationResultCode.AlreadyPermanentlyDeleted ||
    resultCode === TrashOperationResultCode.NoLongerDirectlyTrashed;

const removeRow = (state: StorymapState, rowId: string): void => {
    state.trashState.rows = state.trashState.rows.filter((x) => x.id !== rowId);
    if (state.trashState.selectedRowId === rowId) {
        state.trashState.selectedRowId = null;
    }
};

//@ts-ignore
registerMiddleware(RestoreCardFromTrash.ACTION_TYPE, middleware);
//@ts-ignore
registerReducer(RestoreCardFromTrash.ACTION_TYPE, reducer);
